import {
  DashboardSelectorModuleItem,
  DashboardSelectorSubmoduleItem,
} from '../types/dashboardSelector';

export type SplitListableModulesResult = {
  selectedModulesList: DashboardSelectorModuleItem[];
  unselectedModulesList: DashboardSelectorModuleItem[];
  selectedCount: number;
  unselectedCount: number;
};

const buildSelectionById = (
  selectedSubmodules: DashboardSelectorSubmoduleItem[],
): Map<string, DashboardSelectorSubmoduleItem> =>
  new Map(selectedSubmodules.map((item) => [item.submoduleId, item]));

/** Merge catalog fields with selection/API state so rows render a single hydrated object. */
const hydrateCatalogSubmodule = (
  catalogSubmodule: DashboardSelectorSubmoduleItem,
  selectionById: Map<string, DashboardSelectorSubmoduleItem>,
): DashboardSelectorSubmoduleItem => {
  const selection = selectionById.get(catalogSubmodule.submoduleId);
  if (!selection) {
    return { ...catalogSubmodule, editable: catalogSubmodule.editable ?? true };
  }
  return { ...catalogSubmodule, ...selection };
};

const normalizeSearch = (search?: string) => search?.trim().toLowerCase() ?? '';

const matchesSearch = (text: string | undefined, normalizedSearch: string) =>
  !normalizedSearch || (text ?? '').toLowerCase().includes(normalizedSearch);

/** Module name match shows all submodules; otherwise only matching submodule names. */
const filterSubmodulesBySearch = (
  module: DashboardSelectorModuleItem,
  normalizedSearch: string,
) => {
  if (!normalizedSearch) {
    return module.submodules;
  }

  if (matchesSearch(module.name, normalizedSearch)) {
    return module.submodules;
  }

  return module.submodules.filter((submodule) => matchesSearch(submodule.name, normalizedSearch));
};

export const splitListableModulesBySelection = (
  listableModules: DashboardSelectorModuleItem[],
  selectedSubmodules: DashboardSelectorSubmoduleItem[],
  search?: string,
): SplitListableModulesResult => {
  const selectedIds = new Set(selectedSubmodules.map((item) => item.submoduleId));
  const selectionById = buildSelectionById(selectedSubmodules);
  const normalizedSearch = normalizeSearch(search);

  const selectedModulesList: DashboardSelectorModuleItem[] = [];
  const unselectedModulesList: DashboardSelectorModuleItem[] = [];
  let selectedCount = 0;
  let unselectedCount = 0;

  for (const module of listableModules) {
    const filteredSubmodules = filterSubmodulesBySearch(module, normalizedSearch);

    if (filteredSubmodules.length === 0) {
      continue;
    }

    const selectedSubmodulesInModule = filteredSubmodules
      .filter((submodule) => selectedIds.has(submodule.submoduleId))
      .map((submodule) => hydrateCatalogSubmodule(submodule, selectionById));

    const unselectedSubmodulesInModule = filteredSubmodules
      .filter((submodule) => !selectedIds.has(submodule.submoduleId))
      .map((submodule) => hydrateCatalogSubmodule(submodule, selectionById));

    if (selectedSubmodulesInModule.length > 0) {
      selectedModulesList.push({
        ...module,
        submodules: selectedSubmodulesInModule,
      });
      selectedCount += selectedSubmodulesInModule.length;
    }

    if (unselectedSubmodulesInModule.length > 0) {
      unselectedModulesList.push({
        ...module,
        submodules: unselectedSubmodulesInModule,
      });
      unselectedCount += unselectedSubmodulesInModule.length;
    }
  }

  return {
    selectedModulesList,
    unselectedModulesList,
    selectedCount,
    unselectedCount,
  };
};
